import React from 'react';
import { useInView } from 'react-intersection-observer';
import { isEmpty } from 'lodash';

import type { AiChatReference, Insight } from '~/components/AiChat/types';
import { useInsightsData } from '~/components/ClaimPage/CliveApp/CliveInsights/contexts/InsightsDataProvider';
import { InsightHistoryCard } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/InsightHistoryCard';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { Text } from '~/components/core/TextComponents';

interface InsightHistoryTimelineProps {
  insight: Insight;
  references: Record<string, AiChatReference>;
}

/**
 * The older versions of one insight, newest first, as a scrollable timeline. The latest version is
 * deliberately absent: it is the card this hangs under, and the read is anchored on its id, so the
 * two are always a clean partition.
 */
export const InsightHistoryTimeline: React.FC<InsightHistoryTimelineProps> = ({ insight, references }) => {
  const { history, loadMoreHistory, retryHistory } = useInsightsData();
  // State rather than a ref: the observer has to be built against the panel, and a ref assignment
  // does not re-render, so with a ref the first sentinel would be observed against the viewport.
  const [scrollContainer, setScrollContainer] = React.useState<HTMLDivElement | null>(null);
  const state = history[insight.insight_key];

  const versions = state?.versions ?? [];
  const hasMore = state?.hasMore ?? false;
  const isLoading = state?.isLoading ?? false;
  const isError = Boolean(state?.isError);

  // Rooted on the panel, not the window: the timeline scrolls inside itself. Not `triggerOnce` —
  // this has to fire again for every page.
  const [sentinelRef, isSentinelInView] = useInView({ root: scrollContainer });

  const { id: insightId, insight_key: insightKey } = insight;
  React.useEffect(() => {
    if (isSentinelInView && hasMore && !isLoading) {
      loadMoreHistory(insight);
    }
    // `insight` is rebuilt by every list read, so depending on the object itself would re-fire this
    // after an unrelated refetch. Its identity only matters here through these two fields.
    //
    // `versions.length` so paging does not hinge on catching the isLoading flip: a response that
    // settles in a microtask batches its true and false into one render, leaving no transition to
    // catch. Real HTTP yields a task first, but the suite's mocks do not - drop this and
    // "pages on the last row rendered, never re-sending a cursor" fails, never loading page two.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSentinelInView, hasMore, isLoading, versions.length, insightId, insightKey, loadMoreHistory]);

  return (
    <div ref={setScrollContainer} className="max-h-[360px] overflow-y-auto pl-24">
      <ol className="relative m-0 flex list-none flex-col gap-12 p-0">
        {versions.map((version, index) => (
          <li key={version.id} className="relative">
            {index < versions.length - 1 ? (
              <span
                data-testid="insight-history-line"
                className="absolute bottom-[-12px] left-[-19px] top-[20px] z-[1] w-[2px] bg-blue-300"
              />
            ) : null}
            <span
              data-testid="insight-history-dot"
              className="absolute left-[-24px] top-[4px] z-[2] h-[12px] w-[12px] rounded-full border border-solid border-blue-600 bg-blue-300"
            />
            <InsightHistoryCard version={version} references={references} />
          </li>
        ))}
      </ol>

      {isLoading || isError ? (
        <div className="flex items-center gap-8 py-8">
          <SkeletonTable rowsCount={1} columnsCount={1} isError={isError} wrapperPadding="0" />
          {/* Paging stops on failure, so recovery has to be offered rather than assumed. */}
          {isError ? (
            <Button
              variant="text"
              className="p-0 font-medium normal-case text-teal-700 hover:bg-transparent"
              onClick={() => retryHistory(insight)}
            >
              Try again
            </Button>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !isError && isEmpty(versions) ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="block py-8">
          No previous versions.
        </Text>
      ) : null}

      {hasMore ? <div ref={sentinelRef} data-testid="insight-history-sentinel" className="h-1" /> : null}
    </div>
  );
};
